import {
    chartRefreshScheduler,
    computeStaleState,
    formatAge,
    FrameBudgetTracker,
    measureFrame,
    RingBuffer,
    getStaleThresholdMs,
    setSnapshot,
    getSnapshot,
    reportChartRenderMetric,
} from '../../charts/engine';
import { renderAreaChart, renderDonutChart, renderLineChart, renderHeatmapGrid } from '../../charts/primitives';
import {
    getDefaultParams,
    runBacktestSimulation,
    type BacktestResult,
} from '../../services/backtest-simulator';
import { isFeatureEnabled } from '../../services/runtime-config';

type BacktestSnapshot = {
    result: BacktestResult;
    pnlSeries: number[];
    lastUpdatedAt: number;
};

type BreakdownEntry = {
  key: string;
  trades: number;
  winRate: number;
  avgPnl: number;
};

const teardownMap = new WeakMap<HTMLElement, () => void>();

function formatINR(value: number): string {
    return `₹${Math.abs(value).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

function calcDrawdownSeries(equity: number[]): number[] {
    let peak = Number.NEGATIVE_INFINITY;
    return equity.map((value) => {
        peak = Math.max(peak, value);
        if (peak <= 0) return 0;
        return ((value - peak) / peak) * 100;
    });
}

function toReturnBuckets(dailyReturns: number[], bucketCount: number): number[] {
    if (dailyReturns.length === 0) return [];
    const size = Math.max(1, Math.floor(dailyReturns.length / bucketCount));
    const out: number[] = [];
    for (let i = 0; i < dailyReturns.length; i += size) {
        const bucket = dailyReturns.slice(i, i + size);
        const avg = bucket.reduce((sum, v) => sum + v, 0) / bucket.length;
        out.push(avg * 100);
    }
    return out.slice(0, bucketCount);
}

function toBreakdownEntries(
  breakdown: Record<string, { trades: number; winRate: number; avgPnl: number }> | undefined,
  fallbackTrades: BacktestResult['trades'],
  keySelector: (trade: BacktestResult['trades'][number]) => string,
): BreakdownEntry[] {
  const fromBreakdown = Object.entries(breakdown || {}).map(([key, value]) => ({
    key,
    trades: Number(value?.trades || 0),
    winRate: Number(value?.winRate || 0),
    avgPnl: Number(value?.avgPnl || 0),
  })).filter((entry) => entry.trades > 0);

  if (fromBreakdown.length > 0) {
    return fromBreakdown;
  }

  const grouped = new Map<string, { trades: number; winners: number; totalPnl: number }>();
  fallbackTrades.forEach((trade) => {
    const key = keySelector(trade) || 'unknown';
    const record = grouped.get(key) || { trades: 0, winners: 0, totalPnl: 0 };
    record.trades += 1;
    if (trade.pnl >= 0) record.winners += 1;
    record.totalPnl += Number(trade.pnlPct || trade.pnl || 0);
    grouped.set(key, record);
  });

  return Array.from(grouped.entries()).map(([key, value]) => ({
    key,
    trades: value.trades,
    winRate: value.trades > 0 ? (value.winners / value.trades) * 100 : 0,
    avgPnl: value.trades > 0 ? value.totalPnl / value.trades : 0,
  }));
}

function toRegimeEdgeCells(entries: BreakdownEntry[]): Array<{ x: number; y: number; value: number }> {
  const cols = 3;
  const top = entries
    .slice()
    .sort((a, b) => b.trades - a.trades)
    .slice(0, 6);

  return top.flatMap((entry, idx) => {
    const x = idx % cols;
    const yBase = Math.floor(idx / cols) * 2;
    return [
      { x, y: yBase, value: entry.winRate - 50 },
      { x, y: yBase + 1, value: entry.avgPnl },
    ];
  });
}

function getExpectancy(result: BacktestResult): number {
  if (!Array.isArray(result.trades) || result.trades.length === 0) return 0;
  const total = result.trades.reduce((sum, trade) => sum + Number(trade.pnlPct || trade.pnl || 0), 0);
  return total / result.trades.length;
}

export function renderPhase2BacktestEngine(container: HTMLElement): void {
    if (!isFeatureEnabled('charts_v2_engine')) {
        container.innerHTML = '<div class="wm-empty">charts_v2_engine flag is disabled.</div>';
        return;
    }

    teardownMap.get(container)?.();

    container.innerHTML = `
      <div class="v2-chart-card glass-card">
        <div class="card-header">
          <h3>Phase 2 Engine: Backtest Analytics</h3>
          <span class="text-xs text-muted">Tier D refresh</span>
        </div>

        <div id="v2-backtest-summary" class="terminal-stat-strip" style="grid-template-columns: repeat(4, minmax(0, 1fr)); margin: 0 0 var(--space-3) 0;"></div>

        <div class="v2-chart-grid">
          <div class="v2-chart-slot">
            <div class="v2-chart-title">Equity Curve</div>
            <canvas id="v2-backtest-equity" class="v2-chart-canvas"></canvas>
          </div>

          <div class="v2-chart-slot">
            <div class="v2-chart-title">Drawdown Curve</div>
            <canvas id="v2-backtest-drawdown" class="v2-chart-canvas"></canvas>
          </div>

          <div class="v2-chart-slot">
            <div class="v2-chart-title">Win/Loss Split</div>
            <canvas id="v2-backtest-split" class="v2-chart-canvas"></canvas>
          </div>

          <div class="v2-chart-slot">
            <div class="v2-chart-title">Rolling Return Grid</div>
            <canvas id="v2-backtest-returns" class="v2-chart-canvas"></canvas>
          </div>

          <div class="v2-chart-slot">
            <div class="v2-chart-title">Net P&L Momentum</div>
            <canvas id="v2-backtest-pnl-series" class="v2-chart-canvas"></canvas>
          </div>

          <div class="v2-chart-slot">
            <div class="v2-chart-title">Regime Edge Grid</div>
            <canvas id="v2-backtest-regime-grid" class="v2-chart-canvas"></canvas>
          </div>

          <div class="v2-chart-slot">
            <div class="v2-chart-title">Regime Stability Meter</div>
            <div id="v2-backtest-regime-stability" class="v2-sector-board"></div>
          </div>

          <div class="v2-chart-slot v2-chart-slot--wide">
            <div class="v2-chart-title">Recent Trades</div>
            <div id="v2-backtest-trades" class="v2-sector-board"></div>
          </div>

          <div class="v2-chart-slot v2-chart-slot--wide">
            <div class="v2-chart-title">Strategy Edge Board</div>
            <div id="v2-backtest-strategy-board" class="v2-sector-board"></div>
          </div>
        </div>

        <div class="v2-chart-meta">
          <span id="v2-backtest-freshness" class="text-xs">Waiting for simulation result...</span>
          <span id="v2-backtest-frame" class="text-xs text-muted" style="margin-left: var(--space-3);">Frame: --</span>
        </div>

        <div id="v2-backtest-stale" class="v2-stale-overlay" style="display:none;">
          Simulation data stale. Rendering cached snapshot.
        </div>
      </div>
    `;

    const summaryNode = container.querySelector('#v2-backtest-summary') as HTMLElement | null;
    const equityCanvas = container.querySelector('#v2-backtest-equity') as HTMLCanvasElement | null;
    const drawdownCanvas = container.querySelector('#v2-backtest-drawdown') as HTMLCanvasElement | null;
    const splitCanvas = container.querySelector('#v2-backtest-split') as HTMLCanvasElement | null;
    const returnsCanvas = container.querySelector('#v2-backtest-returns') as HTMLCanvasElement | null;
    const pnlSeriesCanvas = container.querySelector('#v2-backtest-pnl-series') as HTMLCanvasElement | null;
    const regimeGridCanvas = container.querySelector('#v2-backtest-regime-grid') as HTMLCanvasElement | null;
    const regimeStabilityNode = container.querySelector('#v2-backtest-regime-stability') as HTMLElement | null;
    const tradesNode = container.querySelector('#v2-backtest-trades') as HTMLElement | null;
    const strategyBoardNode = container.querySelector('#v2-backtest-strategy-board') as HTMLElement | null;
    const freshnessNode = container.querySelector('#v2-backtest-freshness') as HTMLElement | null;
    const frameNode = container.querySelector('#v2-backtest-frame') as HTMLElement | null;
    const staleNode = container.querySelector('#v2-backtest-stale') as HTMLElement | null;

    if (!summaryNode || !equityCanvas || !drawdownCanvas || !splitCanvas || !returnsCanvas || !pnlSeriesCanvas || !regimeGridCanvas || !regimeStabilityNode || !tradesNode || !strategyBoardNode || !freshnessNode || !frameNode || !staleNode) return;

    const snapshotKey = 'phase2:backtest-engine';
    const staleThresholdMs = getStaleThresholdMs('D');
    const frameTracker = new FrameBudgetTracker(120, 16);
    const pnlSeries = new RingBuffer<number>(120);

    let lastResult: BacktestResult | null = null;
    let lastUpdatedAt: number | null = null;

    const draw = (result: BacktestResult) => {
        const sample = measureFrame('backtest-engine', () => {
            const metrics = result.metrics || {};
            const netPnl = Number(metrics.totalPnl || 0);
            const winRate = Number(metrics.winRate || 0);
            const maxDd = Number(metrics.maxDrawdown || 0);
            const expectancy = getExpectancy(result);

            summaryNode.innerHTML = `
              <div class="terminal-stat"><span class="terminal-stat__label">Net P&L</span><span class="terminal-stat__value ${netPnl >= 0 ? 'terminal-text--up' : 'terminal-text--down'}">${netPnl >= 0 ? '+' : '-'}${formatINR(netPnl)}</span></div>
              <div class="terminal-stat"><span class="terminal-stat__label">Win Rate</span><span class="terminal-stat__value">${winRate.toFixed(2)}%</span></div>
              <div class="terminal-stat"><span class="terminal-stat__label">Max Drawdown</span><span class="terminal-stat__value terminal-text--down">${maxDd.toFixed(2)}%</span></div>
              <div class="terminal-stat"><span class="terminal-stat__label">Expectancy</span><span class="terminal-stat__value ${expectancy >= 0 ? 'terminal-text--up' : 'terminal-text--down'}">${expectancy >= 0 ? '+' : ''}${expectancy.toFixed(2)}%</span></div>
            `;

            renderLineChart(equityCanvas, result.equityCurve, '#5aa6ff');
            renderAreaChart(drawdownCanvas, calcDrawdownSeries(result.equityCurve), '#d94736', 'rgba(217,71,54,0.18)');

            const winners = result.trades.filter(t => t.pnl >= 0).length;
            const losers = result.trades.filter(t => t.pnl < 0).length;
            renderDonutChart(splitCanvas, [winners, losers], ['#15b57a', '#d94736']);

            const buckets = toReturnBuckets(result.dailyReturns, 12);
            const cols = 6;
            const rows = 2;
            const cells = buckets.map((value, idx) => ({
                x: idx % cols,
                y: Math.floor(idx / cols),
                value,
            }));
            renderHeatmapGrid(returnsCanvas, cells, cols, rows);

            renderLineChart(pnlSeriesCanvas, pnlSeries.toArray(), '#ff9f43');

            const regimeEntries = toBreakdownEntries(result.regimeBreakdown, result.trades, trade => trade.regime);
            const regimeCells = toRegimeEdgeCells(regimeEntries);
            renderHeatmapGrid(regimeGridCanvas, regimeCells, 3, 4);

            const totalRegimeTrades = Math.max(1, regimeEntries.reduce((sum, entry) => sum + entry.trades, 0));
            const dominantRegimeShare = regimeEntries.length > 0
              ? (Math.max(...regimeEntries.map((entry) => entry.trades)) / totalRegimeTrades) * 100
              : 0;
            const weightedWinRate = regimeEntries.reduce((sum, entry) => sum + (entry.winRate * entry.trades), 0) / totalRegimeTrades;
            const stabilityTone = dominantRegimeShare >= 55 ? 'terminal-text--down' : dominantRegimeShare >= 40 ? '' : 'terminal-text--up';
            regimeStabilityNode.innerHTML = `
              <div class="terminal-list__row"><span>Dominant Regime Share</span><strong class="${stabilityTone}">${dominantRegimeShare.toFixed(1)}%</strong></div>
              <div class="terminal-list__row"><span>Weighted Regime Win Rate</span><strong>${weightedWinRate.toFixed(1)}%</strong></div>
            `;

            const recentTrades = result.trades.slice(-10).reverse();
            tradesNode.innerHTML = recentTrades.map((trade) => {
                const tone = trade.pnl >= 0 ? 'terminal-text--up' : 'terminal-text--down';
                return `<div class="terminal-list__row"><span>${trade.strategy} <span class="text-xs text-muted">${trade.entryDate} → ${trade.exitDate}</span></span><strong class="${tone}">${trade.pnl >= 0 ? '+' : ''}${trade.pnl.toFixed(2)}%</strong></div>`;
            }).join('');

            const strategyEntries = toBreakdownEntries(result.strategyBreakdown, result.trades, trade => trade.strategy)
              .sort((a, b) => {
                const leftScore = a.winRate * 0.6 + a.avgPnl * 6 + Math.log10(Math.max(1, a.trades)) * 5;
                const rightScore = b.winRate * 0.6 + b.avgPnl * 6 + Math.log10(Math.max(1, b.trades)) * 5;
                return rightScore - leftScore;
              })
              .slice(0, 6);

            strategyBoardNode.innerHTML = strategyEntries.length > 0
              ? strategyEntries.map((entry) => {
                const pnlTone = entry.avgPnl >= 0 ? 'terminal-text--up' : 'terminal-text--down';
                return `<div class="terminal-list__row"><span>${entry.key} <span class="text-xs text-muted">${entry.trades} trades</span></span><strong>${entry.winRate.toFixed(1)}% WR | <span class="${pnlTone}">${entry.avgPnl >= 0 ? '+' : ''}${entry.avgPnl.toFixed(2)}%</span></strong></div>`;
              }).join('')
              : '<div class="wm-empty">No strategy breakdown available.</div>';
        });

        frameTracker.push(sample.label, sample.durationMs);
        const avg = frameTracker.getAverageMs();
        const within = frameTracker.isWithinBudget(sample.durationMs);
        frameNode.textContent = `Frame: ${sample.durationMs.toFixed(2)}ms | avg ${avg.toFixed(2)}ms | budget ${within ? 'OK' : 'OVER'}`;

        reportChartRenderMetric({
            id: 'phase2-backtest',
            label: 'Backtest Engine',
            latestMs: sample.durationMs,
            averageMs: avg,
            budgetMs: frameTracker.getBudgetMs(),
            withinBudget: within,
        });

        const stale = computeStaleState(lastUpdatedAt, staleThresholdMs);
        staleNode.style.display = stale.stale ? 'flex' : 'none';
        freshnessNode.textContent = stale.stale
            ? `Stale: ${formatAge(stale.ageMs)} old`
            : `Live update ${new Date(lastUpdatedAt || Date.now()).toLocaleTimeString('en-IN')}`;
    };

    const persist = () => {
        if (!lastResult) return;
        setSnapshot<BacktestSnapshot>(snapshotKey, {
            result: lastResult,
            pnlSeries: pnlSeries.toArray(),
            lastUpdatedAt: lastUpdatedAt || Date.now(),
        });
    };

    const update = async () => {
        try {
            const status = await runBacktestSimulation(getDefaultParams());
            if (status.state !== 'complete' || !status.result) throw new Error(status.error || 'simulation failed');

            const netPnl = Number(status.result.metrics?.totalPnl || 0);
            pnlSeries.push(netPnl);
            lastResult = status.result;
            lastUpdatedAt = Date.now();
            persist();
            draw(status.result);
        } catch {
            if (lastResult) draw(lastResult);
        }
    };

    const persisted = getSnapshot<BacktestSnapshot>(snapshotKey);
    if (persisted?.result) {
        lastResult = persisted.result;
        persisted.pnlSeries.forEach(v => pnlSeries.push(v));
        lastUpdatedAt = persisted.lastUpdatedAt;
        draw(persisted.result);
    }

    const taskId = `phase2-backtest-${Math.random().toString(36).slice(2)}`;
    const unregister = chartRefreshScheduler.register(taskId, 'D', update, 60000);

    teardownMap.set(container, () => {
        unregister();
    });
}
